import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/Logo";
import { Fingerprint, Loader2, ShieldCheck, AlertTriangle, AlertCircle, Eye, EyeOff, Lock, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Pantalla de recuperación accesible por link único.
 * Flujo:
 *   1) /recuperar?token=XXX → valida token
 *   2) Si el dispositivo soporta biometría (WebAuthn), invita a verificar
 *   3) Usuario fija nueva contraseña → cuenta desbloqueada
 */
export default function RecoverAccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [stage, setStage] = useState("validating"); // validating | invalid | biometric | reset | done
  const [errorMsg, setErrorMsg] = useState("");
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [biometricUsed, setBiometricUsed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const verify = async () => {
      if (!token) { setStage("invalid"); setErrorMsg("Link inválido."); return; }
      try {
        const { data } = await api.post("/auth/verify-unlock-token", { token });
        setEmail(data.email);
        // Si el navegador soporta WebAuthn, vamos al paso biométrico
        const biometricAvailable = typeof window !== "undefined"
          && window.PublicKeyCredential
          && typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === "function";
        if (biometricAvailable) {
          try {
            const ok = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
            setStage(ok ? "biometric" : "reset");
          } catch { setStage("reset"); }
        } else {
          setStage("reset");
        }
      } catch (e) {
        setStage("invalid");
        setErrorMsg(formatApiErrorDetail(e.response?.data?.detail) || "Link inválido o expirado.");
      }
    };
    verify();
  }, [token]);

  const runBiometric = async () => {
    if (!window.PublicKeyCredential) { setStage("reset"); return; }
    try {
      // Reto efímero local — la validación final es del servidor mediante el token único.
      const challenge = new Uint8Array(32);
      window.crypto.getRandomValues(challenge);
      await navigator.credentials.create({
        publicKey: {
          challenge,
          rp: { name: "NEUROMATEMÁTICAS JASQ®" },
          user: {
            id: new TextEncoder().encode(email || "user"),
            name: email || "user",
            displayName: email || "Usuario",
          },
          pubKeyCredParams: [{ type: "public-key", alg: -7 }, { type: "public-key", alg: -257 }],
          timeout: 60000,
          authenticatorSelection: { userVerification: "required", residentKey: "discouraged" },
          attestation: "none",
        },
      });
      setBiometricUsed(true);
      toast.success("Verificación biométrica completada");
      setStage("reset");
    } catch (e) {
      // El usuario canceló o el dispositivo no tiene biometría — permitimos continuar.
      setStage("reset");
    }
  };

  const [reuseError, setReuseError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setReuseError("");
    if (pwd.length < 6) { toast.error("La contraseña debe tener al menos 6 caracteres."); return; }
    if (pwd !== pwd2) { toast.error("Las contraseñas no coinciden."); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/unlock", { token, new_password: pwd, biometric_used: biometricUsed });
      setStage("done");
      toast.success("Cuenta desbloqueada. Ya puedes iniciar sesión.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = formatApiErrorDetail(detail) || "No fue posible restablecer la contraseña.";
      if (detail && typeof detail === "object" && detail.code === "PASSWORD_REUSED") {
        setReuseError(msg);
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen jasq-bg-playful flex items-center justify-center p-4 sm:p-8" data-testid="recover-page">
      <div className="jasq-blob bg-[#FFCC28]" style={{ width: 280, height: 280, top: -60, right: -40 }} />
      <div className="jasq-blob bg-[#A43790]" style={{ width: 240, height: 240, bottom: -50, left: -30, animationDelay: "2s" }} />

      <div className="relative z-10 w-full max-w-md">
        <div className="mb-6 flex justify-center"><Logo size={48} /></div>
        <div className="jasq-card p-7 space-y-5">

          {stage === "validating" && (
            <div className="text-center py-8" data-testid="recover-validating">
              <Loader2 size={32} className="mx-auto animate-spin text-[#A43790]" />
              <p className="text-sm text-[#6E7591] mt-3">Validando link…</p>
            </div>
          )}

          {stage === "invalid" && (
            <div className="text-center py-6 space-y-3" data-testid="recover-invalid">
              <span className="w-14 h-14 rounded-full bg-[#FFE9F0] text-[#A52258] flex items-center justify-center mx-auto">
                <AlertTriangle size={26} />
              </span>
              <h2 className="font-display text-xl font-extrabold text-[#1F2547]">Link inválido o expirado</h2>
              <p className="text-sm text-[#6E7591]">{errorMsg}</p>
              <button onClick={() => navigate("/")} className="jasq-btn-purple inline-flex items-center gap-2 mx-auto">
                Volver al inicio
              </button>
            </div>
          )}

          {stage === "biometric" && (
            <div className="text-center py-2 space-y-4" data-testid="recover-biometric">
              <span className="w-16 h-16 rounded-full bg-[#A43790]/10 text-[#A43790] flex items-center justify-center mx-auto">
                <Fingerprint size={32} />
              </span>
              <div>
                <h2 className="font-display text-xl font-extrabold text-[#1F2547]">Verificación biométrica</h2>
                <p className="text-sm text-[#6E7591] mt-1">
                  Usa tu huella, rostro o PIN de dispositivo para confirmar que eres tú.
                </p>
              </div>
              <button onClick={runBiometric} className="jasq-btn-purple w-full justify-center inline-flex items-center gap-2" data-testid="recover-biometric-btn">
                <Fingerprint size={16} /> Verificar identidad
              </button>
              <button onClick={() => setStage("reset")} className="text-xs text-[#6E7591] hover:text-[#1F2547] font-bold" data-testid="recover-skip-biometric">
                Continuar sin biometría
              </button>
            </div>
          )}

          {stage === "reset" && (
            <form onSubmit={submit} className="space-y-4" data-testid="recover-reset-form">
              <div className="flex items-center gap-3">
                <span className="w-12 h-12 rounded-full bg-[#FFCC28]/20 text-[#A43790] flex items-center justify-center">
                  <ShieldCheck size={22} />
                </span>
                <div>
                  <h2 className="font-display text-lg font-extrabold text-[#1F2547]">Restablecer contraseña</h2>
                  <p className="text-xs text-[#6E7591]">{email}</p>
                </div>
              </div>
              {biometricUsed && (
                <div className="text-xs text-[#066837] bg-[#E9F8EF] border border-[#B6E5C8] rounded-lg px-3 py-2 flex items-center gap-2">
                  <CheckCircle2 size={14} /> Biometría verificada
                </div>
              )}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-[#4C5478] tracking-wide uppercase">Nueva contraseña</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E7591]" />
                  <input type={showPwd ? "text" : "password"} value={pwd} onChange={(e) => setPwd(e.target.value)}
                    minLength={6} required
                    placeholder="Mínimo 6 caracteres"
                    autoComplete="new-password"
                    data-testid="recover-pwd"
                    className="w-full pl-10 pr-12 py-3 rounded-xl border-2 border-[#E1E5F0] focus:border-[#A43790] outline-none font-medium" />
                  <button type="button" onClick={() => setShowPwd((s) => !s)} tabIndex={-1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6E7591]">
                    {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-[#4C5478] tracking-wide uppercase">Repite la contraseña</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E7591]" />
                  <input type={showPwd ? "text" : "password"} value={pwd2} onChange={(e) => setPwd2(e.target.value)}
                    minLength={6} required
                    placeholder="Confirma la contraseña"
                    autoComplete="new-password"
                    data-testid="recover-pwd2"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-[#E1E5F0] focus:border-[#A43790] outline-none font-medium" />
                </div>
              </div>
              {reuseError && (
                <div className="rounded-xl border-2 border-[#FAD0DE] bg-[#FFE9F0] text-[#A52258] px-3 py-2 text-xs font-bold inline-flex items-start gap-2" data-testid="recover-reuse-error">
                  <AlertCircle size={14} className="mt-0.5 shrink-0" />
                  <span>{reuseError}</span>
                </div>
              )}
              <button type="submit" disabled={submitting}
                data-testid="recover-submit"
                className="jasq-btn-primary w-full justify-center inline-flex items-center gap-2">
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={14} />}
                Desbloquear cuenta
              </button>
            </form>
          )}

          {stage === "done" && (
            <div className="text-center py-6 space-y-3" data-testid="recover-done">
              <span className="w-14 h-14 rounded-full bg-[#E9F8EF] text-[#05A859] flex items-center justify-center mx-auto">
                <CheckCircle2 size={28} />
              </span>
              <h2 className="font-display text-xl font-extrabold text-[#1F2547]">¡Listo!</h2>
              <p className="text-sm text-[#6E7591]">Tu contraseña fue restablecida y tu cuenta está desbloqueada.</p>
              <button onClick={() => navigate("/")} className="jasq-btn-purple inline-flex items-center gap-2 mx-auto">
                Iniciar sesión
              </button>
            </div>
          )}
        </div>
        <p className="text-center text-xs text-[#6E7591] mt-4">
          ¿Problemas? Contacta a soporte@neuromatematicasjasq.com
        </p>
      </div>
    </div>
  );
}
